import { useEffect, useState } from "react";
import { FaPlus, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import Button from "../../components/Button";
import Card from "../../components/Card";
import LoadingSpinner from "../../components/LoadingSpinner";
import {
  DAYS_OF_WEEK,
  EXPERTISE_LEVELS,
} from "../../constants/bookingConstants";
import UserDetailView from "../../components/UserDetailView";
import { useAuth } from "../../context/AuthContext";
import { api } from "../../services/api";

export default function TutorProfile() {
  const { user, checkAuth } = useAuth();
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    bio: "",
    phone: "",
    availability: [],
    subjects: [],
  });

  // Add a loading state for subjects
  useEffect(() => {
    const fetchSubjects = async () => {
      setLoading(true);
      try {
        const response = await api.get("/subjects");
        setSubjects(response.subjects);
      } catch (error) {
        console.error("Error fetching subjects:", error);
        toast.error("Failed to load subjects");
      } finally {
        setLoading(false);
      }
    };

    fetchSubjects();
  }, []);

  // Refresh user auth data to ensure profile is loaded
  useEffect(() => {
    const refreshAuth = async () => {
      try {
        await checkAuth();
      } catch (error) {
        console.error("Error refreshing auth:", error);
      }
    };
    refreshAuth();
  }, [checkAuth]);

  // Update form data when user profile changes
  useEffect(() => {
    if (user?.profile) {
      setFormData({
        bio: user.profile.bio || "",
        phone: user.phone || "",
        availability: user.profile.availability || [],
        subjects: user.profile.subjects || [],
      });
    }
  }, [user]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const addAvailability = () => {
    setFormData({
      ...formData,
      availability: [
        ...formData.availability,
        { dayOfWeek: 1, startTime: "09:00", endTime: "17:00" },
      ],
    });
  };

  const updateAvailability = (index, field, value) => {
    const newAvailability = [...formData.availability];
    newAvailability[index] = { ...newAvailability[index], [field]: value };
    setFormData({ ...formData, availability: newAvailability });
  };

  const removeAvailability = (index) => {
    setFormData({
      ...formData,
      availability: formData.availability.filter((_, i) => i !== index),
    });
  };

  const addSubject = () => {
    setFormData({
      ...formData,
      subjects: [
        ...formData.subjects,
        { subjectId: "", expertiseLevel: "intermediate" },
      ],
    });
  };

  const updateSubject = (index, field, value) => {
    const newSubjects = [...formData.subjects];
    newSubjects[index] = { ...newSubjects[index], [field]: value };
    setFormData({ ...formData, subjects: newSubjects });
  };

  const removeSubject = (index) => {
    setFormData({
      ...formData,
      subjects: formData.subjects.filter((_, i) => i !== index),
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Filter out incomplete subjects
      const validSubjects = formData.subjects.filter((s) => s.subjectId);

      await api.put(`/users/${user.id}/profile`, {
        phone: formData.phone,
        profile: {
          bio: formData.bio,
          availability: formData.availability,
          subjects: validSubjects,
        },
      });
      toast.success("Profile updated successfully!");
      await checkAuth();
      setEditing(false);
    } catch (error) {
      toast.error(error.error || "Failed to update profile");
    } finally {
      setLoading(false);
    }
  };


  // Add a loading spinner for better UX
  if (loading && subjects.length === 0) {
    return <LoadingSpinner size="lg" className="py-16" />;
  }

  if (!user) {
    return <LoadingSpinner size="lg" className="py-16" />;
  }

  return (
    <>
      <UserDetailView
        headerTitle="Tutor Profile"
        isNewUser={false}
        user={user}
        editing={editing}
        onEdit={() => setEditing(true)}
        onSave={handleSubmit}
        onCancel={() => setEditing(false)}
        formData={{
          firstName: user.name?.first || "",
          lastName: user.name?.last || "",
          email: user.email || "",
          phone: formData.phone,
          role: user.role,
        }}
        onChange={(e) => {
          if (e.target.name === "phone") {
            setFormData((prev) => ({ ...prev, phone: e.target.value }));
          }
        }}
        loading={loading}
        showStats={false}
        showStatus={false}
        canEdit={true}
        canResetPassword={false}
        isAdminForm={false}
      />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        {editing && (
          <Card>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-text opacity-80 mb-2">
                  Bio
                </label>
                <textarea
                  name="bio"
                  rows="4"
                  className="w-full px-3 py-2 border border-border rounded-md focus:outline-none focus:ring-primary focus:border-primary bg-surface text-text"
                  value={formData.bio}
                  onChange={handleChange}
                  placeholder="Tell students about your teaching experience..."
                />
              </div>

              <div>
                <div className="flex justify-between items-center mb-4">
                  <label className="block text-sm font-medium text-text opacity-80">
                    Subjects
                  </label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={addSubject}
                    className="flex items-center gap-2"
                  >
                    <FaPlus className="w-3 h-3" />
                    Add Subject
                  </Button>
                </div>
                {formData.subjects.map((subject, index) => (
                  <div key={index} className="flex gap-4 mb-2 items-center">
                    <select
                      value={subject.subjectId?._id || subject.subjectId}
                      onChange={(e) =>
                        updateSubject(index, "subjectId", e.target.value)
                      }
                      className="flex-1 px-3 py-2 border border-border rounded-md bg-surface text-text"
                    >
                      <option value="">Select Subject</option>
                      {subjects.map((s) => (
                        <option key={s._id} value={s._id}>
                          {s.name} ({s.code})
                        </option>
                      ))}
                    </select>
                    <select
                      value={subject.expertiseLevel}
                      onChange={(e) =>
                        updateSubject(index, "expertiseLevel", e.target.value)
                      }
                      className="px-3 py-2 border border-border rounded-md bg-surface text-text"
                    >
                      {EXPERTISE_LEVELS.map((level) => (
                        <option key={level.value} value={level.value}>
                          {level.label}
                        </option>
                      ))}
                    </select>
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      onClick={() => removeSubject(index)}
                      className="flex items-center gap-2"
                    >
                      <FaTrash className="w-3 h-3" />
                      Remove
                    </Button>
                  </div>
                ))}
                {formData.subjects.length === 0 && (
                  <p className="text-text/70 text-sm">
                    No subjects selected. Click "Add Subject" to add.
                  </p>
                )}
              </div>

              <div>
                <div className="flex justify-between items-center mb-4">
                  <label className="block text-sm font-medium text-text opacity-80">
                    Availability
                  </label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={addAvailability}
                    className="flex items-center gap-2"
                  >
                    <FaPlus className="w-3 h-3" />
                    Add Time Slot
                  </Button>
                </div>
                {formData.availability.map((slot, index) => (
                  <div key={index} className="flex gap-4 mb-2 items-center">
                    <select
                      value={slot.dayOfWeek}
                      onChange={(e) =>
                        updateAvailability(
                          index,
                          "dayOfWeek",
                          parseInt(e.target.value)
                        )
                      }
                      className="px-3 py-2 border border-border rounded-md bg-surface text-text"
                    >
                      {DAYS_OF_WEEK.map((day) => (
                        <option key={day.value} value={day.value}>
                          {day.label}
                        </option>
                      ))}
                    </select>
                    <input
                      type="time"
                      value={slot.startTime}
                      onChange={(e) =>
                        updateAvailability(index, "startTime", e.target.value)
                      }
                      className="px-3 py-2 border border-border rounded-md bg-surface text-text"
                    />
                    <span className="text-text">to</span>
                    <input
                      type="time"
                      value={slot.endTime}
                      onChange={(e) =>
                        updateAvailability(index, "endTime", e.target.value)
                      }
                      className="px-3 py-2 border border-border rounded-md bg-surface text-text"
                    />
                    <Button
                      type="button"
                      variant="danger"
                      size="sm"
                      onClick={() => removeAvailability(index)}
                      className="flex items-center gap-2"
                    >
                      <FaTrash className="w-3 h-3" />
                      Remove
                    </Button>
                  </div>
                ))}
                {formData.availability.length === 0 && (
                  <p className="text-text/70 text-sm">
                    No availability set. Click "Add Time Slot" to add.
                  </p>
                )}
              </div>

              <Button onClick={handleSubmit} loading={loading}>
                Save Profile
              </Button>
            </div>
          </Card>
        )}
      </div>
    </>
  );
}
